import React, { FC, useMemo } from 'react';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { Typography } from '@cp/ds/src/components/typography';
import { ClientOnlineStatusTag, distanceStrategy } from '@cp/entities/clientOnlineStatus';
import { getInitialParticipant, useCurrentUserContext } from '@cp/entities/dialog';
import { DialogMemberType } from '@common/model/dialog/dialogMemberType';
import { Stack } from '@cp/ds/src/components/stack';
import { LocationIcon } from '@cp/ds/src/icons/location';
import { coreLocationApi } from '@cp/entities/location';
import { ClientType } from '@common/model/client/clientType';
import { DialogViewModel } from '@common/model/dialog/dialogViewModel';
import { RubSolidIcon } from '@cp/ds/src/icons';
import { RoutedLink } from 'src/shared/ui/link';
import { coreClientProfileApi } from 'src/entities/clientProfile/api';
import { DialogParticipantInfo } from 'src/entities/dialogParticipanInfo';
import { ClientProfileTags, useClientProfileTags } from 'src/entities/clientProfileTags';
import { useViewResume } from 'src/features/resume/view';
import { AdminRoutePath, useAdminRouteResolver } from 'src/entities/adminRoute';
import { NotificationSubscriptions } from 'src/entities/client';

export interface DialogHeaderProps {
  dialog?: DialogViewModel;
}

export const DialogHeader: FC<DialogHeaderProps> = ({ dialog }) => {
  const userProvider = useCurrentUserContext();
  const participant = getInitialParticipant(dialog?.members ?? [], userProvider.getUser());
  const { data: clientProfile } = coreClientProfileApi.useGetClientProfileQuery(
    { clientId: participant?.entityId as number },
    { skip: !participant || participant.type !== DialogMemberType.Client },
  );
  const { data: clientCity } = coreLocationApi.useGetCityByIdQuery({ id: clientProfile?.cityId as number }, { skip: !clientProfile });
  const clientProfileTags = useClientProfileTags(clientProfile, { tagTypes: ['career', 'sex', 'age'] });
  const [viewResume] = useViewResume();
  const adminRoute = useAdminRouteResolver();
  const profileUrl = useMemo(
    () =>
      clientProfile
        ? adminRoute.resolve({
            route: clientProfile.type === ClientType.Employer ? AdminRoutePath.Employers : AdminRoutePath.Specialists,
            params: { id: clientProfile.id },
          })
        : '',
    [clientProfile, adminRoute],
  );

  return (
    <Stack direction="column" sx={{ alignItems: 'stretch' }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', marginBottom: 1 }}>
        {clientCity ? (
          <Stack direction="row" spacing={0.5}>
            <LocationIcon />
            <Typography variant="caption bold">{clientCity.translation.name}</Typography>
          </Stack>
        ) : (
          <Skeleton width={140} />
        )}

        {participant ? <ClientOnlineStatusTag profile={participant} strategy={distanceStrategy} /> : undefined}
        {clientProfile?.subscriptionId && <RubSolidIcon />}
      </Stack>

      {participant ? (
        <DialogParticipantInfo
          avatarSize={60}
          lowerBlock={
            participant?.type === DialogMemberType.Client && clientProfile ? (
              <Stack direction="row" sx={{ marginTop: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack alignItems="center" direction="row" gap={1}>
                  <ClientProfileTags tags={clientProfileTags} />
                  {clientProfile?.notificationPlatforms && <NotificationSubscriptions data={clientProfile.notificationPlatforms} />}
                </Stack>
                <RoutedLink
                  color="pink"
                  href={profileUrl}
                  onClick={(e) => {
                    if (clientProfile.type === ClientType.Specialist && clientProfile.mainResumeId) {
                      e.preventDefault();
                      viewResume(clientProfile.mainResumeId);
                    }
                  }}
                  size="small"
                >
                  Открыть профиль
                </RoutedLink>
              </Stack>
            ) : undefined
          }
          participant={participant}
          rightBlockSx={{ flexGrow: 1 }}
        />
      ) : (
        <Skeleton height={48} width={140} />
      )}
    </Stack>
  );
};
